import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, MapPinIcon, ClockIcon, ShieldCheckIcon, CheckCircleIcon, ArrowRightIcon, AlertCircleIcon } from 'lucide-react';
// ─── WEB3FORMS SETUP ───
// 1. Go to https://web3forms.com
// 2. Enter: njfourseasonsguttercleaning@gmail.com
// 3. Check your email for the access key
// 4. Replace the key below
const WEB3FORMS_ACCESS_KEY = 'YOUR_ACCESS_KEY_HERE';
export function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    address: '',
    message: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.id]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: 'New Website Lead - NJ Four Seasons',
          from_name: 'NJ Four Seasons Website',
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          service: formData.service,
          address: formData.address,
          message: formData.message || 'No message provided'
        })
      });
      const result = await response.json();
      if (result.success) {
        setIsSuccess(true);
        setFormData({
          name: '',
          phone: '',
          email: '',
          service: '',
          address: '',
          message: ''
        });
      } else {
        setError('Something went wrong. Please call us directly at (855) 471-1600.');
      }
    } catch (err) {
      setError('Network error. Please call us directly at (855) 471-1600.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="contact" className="relative py-24 bg-gray-50 overflow-hidden" data-id="element-44">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at 20% 50%, rgba(11,122,196,0.06) 0%, transparent 50%), radial-gradient(ellipse at 80% 50%, rgba(122,201,67,0.04) 0%, transparent 50%)'
    }} data-id="element-45" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-46">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-47">
          <p className="text-[#0B7AC4] font-bold uppercase tracking-widest text-sm mb-3" data-id="element-48">
            Get Started
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-[#0A1628] mb-4" data-id="element-49">
            Get Your Free Estimate
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#0B7AC4] to-[#7AC943] mx-auto rounded-full mb-6" data-id="element-50" />
          <p className="text-xl text-gray-500 max-w-2xl mx-auto font-light" data-id="element-51">
            Fill out the form or call us directly. We respond to all inquiries
            within 24 hours.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 rounded-3xl overflow-hidden shadow-2xl border border-gray-100" data-id="element-52">
          {/* Form */}
          <div className="lg:col-span-3 bg-white p-8 md:p-12" data-id="element-53">
            {isSuccess ? <motion.div initial={{
            opacity: 0,
            scale: 0.92
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            ease: [0.22, 1, 0.36, 1]
          }} className="h-full flex flex-col items-center justify-center text-center py-16" data-id="element-54">
                <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.2,
              type: 'spring',
              stiffness: 200
            }} className="w-24 h-24 rounded-full flex items-center justify-center mb-8" style={{
              background: 'rgba(122,201,67,0.15)',
              border: '2px solid rgba(122,201,67,0.4)'
            }} data-id="element-55">
                  <CheckCircleIcon className="w-12 h-12 text-[#7AC943]" data-id="element-56" />
                </motion.div>
                <h3 className="text-3xl font-black text-[#0A1628] mb-4" data-id="element-57">
                  Thanks! We'll contact you shortly.
                </h3>
                <p className="text-lg text-gray-500 mb-8 max-w-sm" data-id="element-58">
                  A member of our team will reach out within 24 hours to provide
                  your free estimate.
                </p>
                <button onClick={() => setIsSuccess(false)} className="text-[#0B7AC4] font-bold hover:underline text-sm" data-id="element-59">
                  Submit another request
                </button>
              </motion.div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-60">
                {error && <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium" data-id="element-61">
                    <AlertCircleIcon className="w-5 h-5 flex-shrink-0" data-id="element-62" />
                    {error}
                  </div>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-63">
                  <div data-id="element-64">
                    <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-65">
                      Full Name
                    </label>
                    <input type="text" id="name" required value={formData.name} onChange={handleChange} placeholder="John Doe" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#0B7AC4] focus:border-[#0B7AC4] outline-none bg-gray-50 focus:bg-white transition-all text-gray-800 placeholder-gray-400" data-id="element-66" />
                  </div>
                  <div data-id="element-67">
                    <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-68">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" required value={formData.phone} onChange={handleChange} placeholder="(555) 123-4567" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#0B7AC4] focus:border-[#0B7AC4] outline-none bg-gray-50 focus:bg-white transition-all text-gray-800 placeholder-gray-400" data-id="element-69" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-70">
                  <div data-id="element-71">
                    <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-72">
                      Email Address
                    </label>
                    <input type="email" id="email" required value={formData.email} onChange={handleChange} placeholder="john@example.com" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#0B7AC4] focus:border-[#0B7AC4] outline-none bg-gray-50 focus:bg-white transition-all text-gray-800 placeholder-gray-400" data-id="element-73" />
                  </div>
                  <div data-id="element-74">
                    <label htmlFor="service" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-75">
                      Service Needed
                    </label>
                    <select id="service" required value={formData.service} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#0B7AC4] focus:border-[#0B7AC4] outline-none bg-gray-50 focus:bg-white transition-all text-gray-800 appearance-none" data-id="element-76">
                      <option value="" data-id="element-77">Select a service...</option>
                      <option data-id="element-78">Gutter Cleaning</option>
                      <option data-id="element-79">Gutter Repair</option>
                      <option data-id="element-80">Gutter Installation</option>
                      <option data-id="element-81">Gutter Guards</option>
                      <option data-id="element-82">Underground Drain Cleaning</option>
                      <option data-id="element-83">Underground Drain Installation</option>
                      <option data-id="element-84">Power Washing</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-85">
                  <label htmlFor="address" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-86">
                    Property Address
                  </label>
                  <input type="text" id="address" required value={formData.address} onChange={handleChange} placeholder="123 Main St, City, NJ" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#0B7AC4] focus:border-[#0B7AC4] outline-none bg-gray-50 focus:bg-white transition-all text-gray-800 placeholder-gray-400" data-id="element-87" />
                </div>

                <div data-id="element-88">
                  <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-89">
                    Message{' '}
                    <span className="text-gray-400 font-normal" data-id="element-90">
                      (Optional)
                    </span>
                  </label>
                  <textarea id="message" rows={4} value={formData.message} onChange={handleChange} placeholder="Tell us about your project, any specific concerns..." className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#0B7AC4] focus:border-[#0B7AC4] outline-none bg-gray-50 focus:bg-white transition-all text-gray-800 placeholder-gray-400 resize-none" data-id="element-91" />
                </div>

                <button type="submit" disabled={isSubmitting} className="btn-primary btn-ripple w-full text-lg flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none" data-id="element-92">
                  {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-93" /> : <>
                      Request Free Estimate{' '}
                      <ArrowRightIcon className="w-5 h-5" data-id="element-94" />
                    </>}
                </button>

                <p className="text-center text-xs text-gray-400" data-id="element-95">
                  🔒 Your information is secure. We never share your data.
                </p>
              </form>}
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2 p-8 md:p-12 relative overflow-hidden" style={{
          background: 'linear-gradient(160deg, #0A1628 0%, #0d1f3c 100%)'
        }} data-id="element-96">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full pointer-events-none" style={{
            background: 'radial-gradient(circle, rgba(11,122,196,0.2) 0%, transparent 70%)'
          }} data-id="element-97" />
            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full pointer-events-none" style={{
            background: 'radial-gradient(circle, rgba(122,201,67,0.15) 0%, transparent 70%)'
          }} data-id="element-98" />

            <div className="relative z-10" data-id="element-99">
              <h3 className="text-2xl font-black text-white mb-2" data-id="element-100">
                Contact Us Directly
              </h3>
              <p className="text-gray-400 text-sm mb-10" data-id="element-101">
                We&apos;re available 7 days a week.
              </p>

              <div className="space-y-8" data-id="element-102">
                <a href="tel:+18554711600" className="flex items-start gap-4 group" data-id="element-103">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110" style={{
                  background: 'rgba(255,212,0,0.15)',
                  border: '1.5px solid rgba(255,212,0,0.4)',
                  boxShadow: '0 0 20px rgba(255,212,0,0.15)'
                }} data-id="element-104">
                    <PhoneIcon className="w-5 h-5 text-[#FFD400]" data-id="element-105" />
                  </div>
                  <div data-id="element-106">
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1" data-id="element-107">
                      Call Us Directly
                    </p>
                    <p className="text-2xl font-black text-white group-hover:text-[#FFD400] transition-colors" data-id="element-108">
                      (855) 471-1600
                    </p>
                    <p className="text-gray-500 text-xs mt-1" data-id="element-109">
                      Alfonseli Then (Owner)
                    </p>
                  </div>
                </a>

                <a href="tel:+18624072386" className="flex items-start gap-4 group" data-id="element-110">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110" style={{
                  background: 'rgba(11,122,196,0.12)',
                  border: '1.5px solid rgba(11,122,196,0.25)'
                }} data-id="element-111">
                    <PhoneIcon className="w-5 h-5 text-[#0B7AC4]" data-id="element-112" />
                  </div>
                  <div data-id="element-113">
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1" data-id="element-114">
                      Office / Admin Line
                    </p>
                    <p className="text-lg font-bold text-gray-300 group-hover:text-[#0B7AC4] transition-colors" data-id="element-115">
                      (862) 407-2386
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-4" data-id="element-116">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                  background: 'rgba(11,122,196,0.15)',
                  border: '1.5px solid rgba(11,122,196,0.3)'
                }} data-id="element-117">
                    <MapPinIcon className="w-5 h-5 text-[#0B7AC4]" data-id="element-118" />
                  </div>
                  <div data-id="element-119">
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1" data-id="element-120">
                      Headquarters
                    </p>
                    <p className="text-white font-semibold leading-relaxed" data-id="element-121">
                      988 E 19th St 2nd Floor
                      <br data-id="element-122" />
                      Paterson, NJ 07501
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-123">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                  background: 'rgba(249,115,22,0.15)',
                  border: '1.5px solid rgba(249,115,22,0.3)'
                }} data-id="element-124">
                    <ClockIcon className="w-5 h-5 text-[#F97316]" data-id="element-125" />
                  </div>
                  <div data-id="element-126">
                    <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1" data-id="element-127">
                      Business Hours
                    </p>
                    <p className="text-white font-semibold" data-id="element-128">
                      Mon–Sat: 7:00 AM – 7:00 PM
                    </p>
                    <p className="text-white font-semibold" data-id="element-129">
                      Sun: 8:00 AM – 5:00 PM
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8" style={{
              borderTop: '1px solid rgba(255,255,255,0.08)'
            }} data-id="element-130">
                <div className="flex items-center gap-3 p-4 rounded-2xl" style={{
                background: 'rgba(122,201,67,0.1)',
                border: '1px solid rgba(122,201,67,0.25)'
              }} data-id="element-131">
                  <ShieldCheckIcon className="w-7 h-7 text-[#7AC943] flex-shrink-0" data-id="element-132" />
                  <div data-id="element-133">
                    <p className="font-black text-white text-sm" data-id="element-134">
                      Licensed & Insured
                    </p>
                    <p className="text-gray-400 text-xs" data-id="element-135">
                      NJHICRA #13VH08498200
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}